"use client";

import { useState, useCallback, useEffect, useRef } from "react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import {
  QUALIFICATIONS_TREE,
  ROLES_TREE,
  findLabelById,
  getAllLeafIds,
  getNodeCheckState,
  type TreeNode,
} from "@/lib/taxonomy";
import { CascadingTree } from "@/app/components/CascadingTree";
import type { CourseRow } from "@/app/actions/courses";

// ═══ HELPERS ═══

// Season-coded gradient pairs for provider initial circles
const SEASON_GRADIENTS: Record<string, { from: string; to: string }> = {
  winter: { from: "#1E3654", to: "#3A5A7A" },
  summer: { from: "#9A7540", to: "#C49A58" },
  festivals: { from: "#5C3D8F", to: "#7E5CB0" },
};
const DEFAULT_GRADIENT = { from: "#1B2840", to: "#344A68" };

function getSeasonGradient(seasonTypes: string[]): { from: string; to: string } {
  if (seasonTypes.length === 0) return DEFAULT_GRADIENT;
  return SEASON_GRADIENTS[seasonTypes[0]] ?? DEFAULT_GRADIENT;
}

function getProviderInitials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
}

function getSeasonLabel(st: string): string {
  switch (st) {
    case "winter":
      return "Winter";
    case "summer":
      return "Summer";
    case "festivals":
      return "Festivals";
    default:
      return st;
  }
}

function getSeasonIcon(st: string): string {
  switch (st) {
    case "winter":
      return "\u2744\uFE0F";
    case "summer":
      return "\u2600\uFE0F";
    case "festivals":
      return "\uD83C\uDFAA";
    default:
      return "";
  }
}

function getSeasonBadgeStyle(st: string): { bg: string; color: string } {
  switch (st) {
    case "winter":
      return { bg: "#E8EBF0", color: "#1B2A4A" };
    case "summer":
      return { bg: "#E8F7FE", color: "#0EA5E9" };
    case "festivals":
      return { bg: "#FDF0EB", color: "#E8835C" };
    default:
      return { bg: "#F0F0F0", color: "#3A3A3A" };
  }
}

function getFormatLabel(f: string): string {
  switch (f) {
    case "online":
      return "Online";
    case "in-person":
      return "In Person";
    case "both":
      return "Online & In Person";
    default:
      return f;
  }
}

function truncateDescription(desc: string | null, maxLen: number): string {
  if (!desc) return "";
  if (desc.length <= maxLen) return desc;
  return desc.slice(0, maxLen).replace(/\s+\S*$/, "") + "...";
}

function buildTreeChips(
  tree: TreeNode[],
  selected: Set<string>
): { key: string; label: string; leafIds: string[] }[] {
  const chips: { key: string; label: string; leafIds: string[] }[] = [];
  function walk(nodes: TreeNode[]) {
    for (const node of nodes) {
      const state = getNodeCheckState(node, selected);
      if (state === "unchecked") continue;
      if (state === "checked" && node.children && node.children.length > 0) {
        chips.push({
          key: node.id,
          label: node.label,
          leafIds: getAllLeafIds(node),
        });
      } else if (node.children && node.children.length > 0) {
        walk(node.children);
      } else if (selected.has(node.id)) {
        chips.push({ key: node.id, label: node.label, leafIds: [node.id] });
      }
    }
  }
  walk(tree);
  return chips;
}

// Inline graduation cap icon for qualification pills
function GraduationCapIcon() {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="shrink-0"
    >
      <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
      <path d="M6 12v5c0 1.1 2.7 3 6 3s6-1.9 6-3v-5" />
    </svg>
  );
}

// ═══ FILTER SIDEBAR ═══

function FilterSidebar({
  searchQuery,
  onSearchChange,
  qualificationIds,
  onQualificationsChange,
  roleIds,
  onRolesChange,
  formatFilter,
  onFormatChange,
  totalCount,
  filteredCount,
  hasAnyFilter,
  onClearAll,
  onClose,
}: {
  searchQuery: string;
  onSearchChange: (v: string) => void;
  qualificationIds: Set<string>;
  onQualificationsChange: (s: Set<string>) => void;
  roleIds: Set<string>;
  onRolesChange: (s: Set<string>) => void;
  formatFilter: string;
  onFormatChange: (v: string) => void;
  totalCount: number;
  filteredCount: number;
  hasAnyFilter: boolean;
  onClearAll: () => void;
  onClose?: () => void;
}) {
  const isMobile = !!onClose;
  const [localSearch, setLocalSearch] = useState(searchQuery);
  const searchTimeout = useRef<NodeJS.Timeout>(undefined);

  function onSearchInput(value: string) {
    setLocalSearch(value);
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      onSearchChange(value);
    }, 300);
  }

  // Sync local search state when URL changes externally (e.g. clear all)
  useEffect(() => {
    setLocalSearch(searchQuery);
  }, [searchQuery]);

  return (
    <div className="flex flex-col h-full">
      <div className="flex justify-between items-start mb-1">
        <h3 className="font-serif text-[20px] text-navy">Find Courses</h3>
        {onClose && (
          <button
            onClick={onClose}
            className="md:hidden w-8 h-8 rounded-sm bg-warm-bg border-none text-lg cursor-pointer flex items-center justify-center"
            aria-label="Close filters"
          >
            &times;
          </button>
        )}
      </div>
      {!isMobile && (
        <p className="text-xs text-text-muted mb-5">
          {hasAnyFilter
            ? `${filteredCount} course${filteredCount !== 1 ? "s" : ""} matching`
            : `${totalCount} course${totalCount !== 1 ? "s" : ""}`}
        </p>
      )}

      {hasAnyFilter && (
        <button
          onClick={onClearAll}
          className="w-full text-[11px] text-text-muted hover:text-gold transition-colors bg-transparent border-none cursor-pointer font-sans mb-4 text-right"
        >
          Clear all filters
        </button>
      )}

      <div className="flex-1 overflow-y-auto space-y-5 pb-4">
        {/* Search */}
        <div>
          <label className="text-[12px] font-semibold text-text-muted uppercase tracking-[0.6px] block mb-2">
            Search
          </label>
          <div className="relative">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              value={localSearch}
              onChange={(e) => onSearchInput(e.target.value)}
              placeholder="Course name, provider..."
              className="w-full pl-9 pr-3 py-2.5 rounded-[10px] border-[1.5px] border-border bg-white text-sm font-sans text-charcoal outline-none focus:border-gold focus:ring-1 focus:ring-gold/20 transition-all"
            />
          </div>
        </div>

        {/* Qualifications */}
        <CascadingTree
          tree={QUALIFICATIONS_TREE}
          selected={qualificationIds}
          onChange={onQualificationsChange}
          label="Qualifications"
          maxHeight="240px"
          searchable
        />

        {/* Roles */}
        <CascadingTree
          tree={ROLES_TREE}
          selected={roleIds}
          onChange={onRolesChange}
          label="Roles"
          maxHeight="240px"
          searchable
        />

        {/* Format */}
        <div>
          <label className="text-[12px] font-semibold text-text-muted uppercase tracking-[0.6px] block mb-2">
            Format
          </label>
          <select
            value={formatFilter}
            onChange={(e) => onFormatChange(e.target.value)}
            className="w-full px-3.5 py-2.5 rounded-[10px] border-[1.5px] border-border bg-white text-sm font-sans text-charcoal cursor-pointer outline-none focus:border-gold focus:ring-1 focus:ring-gold/20 transition-all"
          >
            <option value="">All formats</option>
            <option value="online">Online</option>
            <option value="in-person">In Person</option>
            <option value="both">Online & In Person</option>
          </select>
        </div>
      </div>

      {/* Mobile apply button */}
      {onClose && (
        <button
          onClick={onClose}
          className="md:hidden w-full py-3 rounded-sm text-[14px] font-semibold bg-gold text-white border-none cursor-pointer font-sans mt-4 hover:bg-gold-light transition-all"
        >
          Show {filteredCount} course{filteredCount !== 1 ? "s" : ""}
        </button>
      )}
    </div>
  );
}

// ═══ MID-PAGE BANNER ═══

function ProviderBanner() {
  return (
    <div className="col-span-1 lg:col-span-2">
      <div className="rounded-[18px] border border-gold/30 bg-gold/[0.06] px-5 py-4">
        <div className="flex items-center justify-between gap-3">
          <p className="text-[13px] text-text-body m-0">
            <span className="font-semibold text-navy">Run a training course?</span>{" "}
            <span className="text-text-muted">List it for free and reach seasonal workers.</span>
          </p>
          <Link
            href="/plan/courses/submit"
            className="shrink-0 px-4 py-2 rounded-sm text-[12px] font-semibold bg-gold text-white no-underline font-sans transition-all hover:bg-gold-light whitespace-nowrap"
          >
            List a Course
          </Link>
        </div>
      </div>
    </div>
  );
}

// ═══ MAIN COMPONENT ═══

export function CoursesContent({
  initialCourses,
  totalCount,
  candidateCount,
  initialParams: _initialParams,
}: {
  initialCourses: CourseRow[];
  totalCount: number;
  candidateCount: number;
  initialParams: {
    qualification?: string;
    season?: string;
    format?: string;
    role?: string;
    q?: string;
  };
}) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  // Derive filter state from URL
  const searchQuery = searchParams.get("q") || "";
  const qualificationIds = searchParams.get("qualification")
    ? new Set(searchParams.get("qualification")!.split(","))
    : new Set<string>();
  const roleIds = searchParams.get("role")
    ? new Set(searchParams.get("role")!.split(","))
    : new Set<string>();
  const seasonFilter = searchParams.get("season") || "";
  const formatFilter = searchParams.get("format") || "";

  const hasAnyFilter =
    searchQuery !== "" ||
    qualificationIds.size > 0 ||
    roleIds.size > 0 ||
    seasonFilter !== "" ||
    formatFilter !== "";

  const filterCount =
    (searchQuery ? 1 : 0) +
    qualificationIds.size +
    roleIds.size +
    (seasonFilter ? 1 : 0) +
    (formatFilter ? 1 : 0);

  const updateParams = useCallback(
    (updates: Record<string, string | null>) => {
      const params = new URLSearchParams(searchParams.toString());
      for (const [key, value] of Object.entries(updates)) {
        if (value) {
          params.set(key, value);
        } else {
          params.delete(key);
        }
      }
      const qs = params.toString();
      router.replace(`/plan/courses${qs ? `?${qs}` : ""}`, { scroll: false });
    },
    [router, searchParams]
  );

  function handleSearchChange(value: string) {
    updateParams({ q: value || null });
  }

  function handleQualificationsChange(selected: Set<string>) {
    const arr = Array.from(selected);
    updateParams({ qualification: arr.length > 0 ? arr.join(",") : null });
  }

  function handleRolesChange(selected: Set<string>) {
    const arr = Array.from(selected);
    updateParams({ role: arr.length > 0 ? arr.join(",") : null });
  }

  function handleSeasonChange(value: string) {
    updateParams({ season: value || null });
  }

  function handleFormatChange(value: string) {
    updateParams({ format: value || null });
  }

  function clearAllFilters() {
    router.replace("/plan/courses", { scroll: false });
  }

  // Client-side filtering
  const filteredCourses = initialCourses.filter((course) => {
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const matches =
        course.title.toLowerCase().includes(q) ||
        course.provider_name.toLowerCase().includes(q) ||
        (course.description ?? "").toLowerCase().includes(q);
      if (!matches) return false;
    }
    if (qualificationIds.size > 0) {
      const hasMatch = course.qualifications.some((qId) => qualificationIds.has(qId));
      if (!hasMatch) return false;
    }
    if (roleIds.size > 0) {
      const hasMatch = course.roles.some((rId) => roleIds.has(rId));
      if (!hasMatch) return false;
    }
    if (seasonFilter) {
      if (!course.season_types.includes(seasonFilter)) return false;
    }
    if (formatFilter) {
      if (course.format !== formatFilter) return false;
    }
    return true;
  });

  // Lock body scroll when mobile filters open
  useEffect(() => {
    document.body.style.overflow = mobileFiltersOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileFiltersOpen]);

  // Build filter chips
  const qualChips = buildTreeChips(QUALIFICATIONS_TREE, qualificationIds);
  const roleChips = buildTreeChips(ROLES_TREE, roleIds);

  const sidebarProps = {
    searchQuery,
    onSearchChange: handleSearchChange,
    qualificationIds,
    onQualificationsChange: handleQualificationsChange,
    roleIds,
    onRolesChange: handleRolesChange,
    formatFilter,
    onFormatChange: handleFormatChange,
    totalCount,
    filteredCount: filteredCourses.length,
    hasAnyFilter,
    onClearAll: clearAllFilters,
  };

  // Determine where to insert the mid-page provider banner (after 4th card in the 2-col grid)
  const bannerInsertIndex = 4;
  const showBanner = filteredCourses.length > bannerInsertIndex;

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-[280px_minmax(0,1fr)] min-h-screen -mt-16 pt-16">
        {/* Desktop Sidebar */}
        <aside className="hidden md:block bg-white border-r border-border-light p-7 sticky top-16 h-[calc(100vh-64px)] overflow-y-auto">
          <FilterSidebar {...sidebarProps} />
        </aside>

        {/* Main content */}
        <div className="p-5 md:px-9 md:py-7">
          {/* Mobile filter toggle */}
          <div className="md:hidden sticky top-16 z-50 bg-warm-bg/95 backdrop-blur-sm border-b border-border-light -mx-5 px-5 py-3 mb-4">
            <div className="flex justify-between items-center mb-2.5">
              <span className="text-sm text-text-muted">
                <strong className="text-charcoal">{filteredCourses.length}</strong>{" "}
                course{filteredCourses.length !== 1 ? "s" : ""}
                {hasAnyFilter ? " matching" : ""}
              </span>
            </div>
            <button
              onClick={() => setMobileFiltersOpen(true)}
              className="w-full py-2.5 rounded-lg text-[14px] font-semibold bg-gold text-white border-none cursor-pointer font-sans transition-all hover:bg-gold-light flex items-center justify-center gap-2 shadow-[0_2px_8px_rgba(28, 138, 153,0.25)]"
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5">
                <line x1="1" y1="3" x2="13" y2="3" />
                <line x1="3" y1="7" x2="11" y2="7" />
                <line x1="5" y1="11" x2="9" y2="11" />
              </svg>
              Filters
              {filterCount > 0 && (
                <span className="w-5 h-5 bg-white/25 rounded-full text-[10px] font-bold flex items-center justify-center">
                  {filterCount}
                </span>
              )}
            </button>
            {/* Quick season group chips */}
            <div className="flex gap-2 mt-2.5">
              {(["winter", "summer", "festivals"] as const).map((s) => {
                const isActive = seasonFilter === s;
                return (
                  <button
                    key={s}
                    onClick={() => handleSeasonChange(isActive ? "" : s)}
                    className={`flex-1 py-1.5 rounded-full text-[12px] font-medium cursor-pointer font-sans transition-all ${
                      isActive
                        ? "bg-gold text-white border border-gold"
                        : "bg-white border border-border-light text-text-body hover:border-gold hover:text-gold active:bg-gold/5"
                    }`}
                  >
                    {getSeasonIcon(s)} {getSeasonLabel(s)}
                  </button>
                );
              })}
            </div>

            {/* Mobile Filter Chips */}
            {hasAnyFilter && (
              <div className="flex flex-wrap gap-2 mt-3">
                {searchQuery && (
                  <FilterChip
                    label={`"${searchQuery}"`}
                    onRemove={() => handleSearchChange("")}
                  />
                )}
                {seasonFilter && (
                  <FilterChip label={getSeasonLabel(seasonFilter)} onRemove={() => handleSeasonChange("")} />
                )}
                {formatFilter && (
                  <FilterChip label={getFormatLabel(formatFilter)} onRemove={() => handleFormatChange("")} />
                )}
                {qualChips.map((chip) => (
                  <FilterChip
                    key={chip.key}
                    label={chip.label}
                    onRemove={() => {
                      const next = new Set(qualificationIds);
                      for (const id of chip.leafIds) next.delete(id);
                      handleQualificationsChange(next);
                    }}
                  />
                ))}
                {roleChips.map((chip) => (
                  <FilterChip
                    key={chip.key}
                    label={chip.label}
                    onRemove={() => {
                      const next = new Set(roleIds);
                      for (const id of chip.leafIds) next.delete(id);
                      handleRolesChange(next);
                    }}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-[2.5px] text-gold mb-3">
                Training
              </div>
              <h1 className="font-serif text-[28px] sm:text-[36px] font-bold text-navy leading-[1.08] tracking-[-0.015em] mb-2">
                Courses & Training
              </h1>
              <p className="text-[14px] text-text-muted max-w-[480px] leading-relaxed">
                Get qualified for your next season. Find courses for ski
                instruction, yachting, hospitality, security and more.
              </p>
            </div>
            <Link
              href="/plan/courses/submit"
              className="shrink-0 px-6 py-3 rounded-sm text-[14px] font-semibold bg-gold text-white no-underline font-sans transition-all hover:bg-gold-light hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgba(28, 138, 153,0.3)] self-start min-h-[44px]"
            >
              List a Course
            </Link>
          </div>

          {/* Desktop filter chips */}
          {hasAnyFilter && (
            <div className="hidden md:flex flex-wrap gap-2 mb-5">
              {searchQuery && (
                <FilterChip
                  label={`"${searchQuery}"`}
                  onRemove={() => handleSearchChange("")}
                />
              )}
              {seasonFilter && (
                <FilterChip label={getSeasonLabel(seasonFilter)} onRemove={() => handleSeasonChange("")} />
              )}
              {formatFilter && (
                <FilterChip label={getFormatLabel(formatFilter)} onRemove={() => handleFormatChange("")} />
              )}
              {qualChips.map((chip) => (
                <FilterChip
                  key={chip.key}
                  label={chip.label}
                  onRemove={() => {
                    const next = new Set(qualificationIds);
                    for (const id of chip.leafIds) next.delete(id);
                    handleQualificationsChange(next);
                  }}
                />
              ))}
              {roleChips.map((chip) => (
                <FilterChip
                  key={chip.key}
                  label={chip.label}
                  onRemove={() => {
                    const next = new Set(roleIds);
                    for (const id of chip.leafIds) next.delete(id);
                    handleRolesChange(next);
                  }}
                />
              ))}
            </div>
          )}

          {/* Desktop quick season group chips */}
          <div className="hidden md:flex gap-2 mb-5">
            {(["winter", "summer", "festivals"] as const).map((s) => {
              const isActive = seasonFilter === s;
              return (
                <button
                  key={s}
                  onClick={() => handleSeasonChange(isActive ? "" : s)}
                  className={`px-4 py-2 rounded-full text-[12.5px] font-medium cursor-pointer font-sans transition-all min-h-[36px] ${
                    isActive
                      ? "bg-gold text-white border border-gold shadow-[0_2px_8px_rgba(28, 138, 153,0.25)]"
                      : "bg-white border border-border-light text-text-body hover:border-gold/40 hover:text-gold"
                  }`}
                >
                  {getSeasonIcon(s)} {getSeasonLabel(s)}
                </button>
              );
            })}
          </div>

          {/* Course Grid */}
          {filteredCourses.length === 0 && totalCount === 0 ? (
            <div className="flex justify-center py-16 md:py-24">
              <div className="max-w-[520px] text-center">
                <div className="w-16 h-16 rounded-[18px] bg-gold/[0.08] flex items-center justify-center mx-auto mb-6">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className="text-gold">
                    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
                    <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
                  </svg>
                </div>
                <h2 className="font-serif text-[24px] sm:text-[28px] text-navy mb-3">
                  Courses coming soon
                </h2>
                <p className="text-[15px] text-text-body leading-[1.7] mb-8 max-w-[440px] mx-auto">
                  We&apos;re building a directory of courses and qualifications for
                  seasonal work. Check back soon or submit a course you know about.
                </p>
                <Link
                  href="/plan/courses/submit"
                  className="bg-gold text-white px-7 py-3 rounded-sm text-[15px] font-semibold no-underline font-sans transition-all inline-flex items-center justify-center gap-2 hover:bg-gold-light hover:-translate-y-0.5"
                >
                  Suggest a Course
                </Link>
              </div>
            </div>
          ) : filteredCourses.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-[15px] text-text-muted mb-2">
                No courses found matching your filters.
              </p>
              <button
                onClick={clearAllFilters}
                className="text-gold text-sm font-semibold bg-transparent border-none cursor-pointer font-sans"
              >
                Clear filters
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3.5">
              {filteredCourses.map((course, i) => (
                <CourseCardWithBanner
                  key={course.id}
                  course={course}
                  index={i}
                  showBanner={showBanner}
                  bannerInsertIndex={bannerInsertIndex}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Navy CTA for training providers */}
      <section className="bg-navy py-14 md:py-16 px-5">
        <div className="max-w-[560px] mx-auto text-center">
          <h2 className="font-serif text-[26px] md:text-[32px] font-normal text-white mb-3">
            Are you a training provider?
          </h2>
          <p className="text-[15px] text-white/70 leading-[1.7] max-w-[440px] mx-auto mb-2">
            Get your courses in front of seasonal workers actively looking for qualifications. Free to list, no commissions.
          </p>
          <p className="text-[14px] text-white/50 mb-7">
            Reach {candidateCount.toLocaleString()}+ seasonal workers looking for qualifications
          </p>
          <Link
            href="/plan/courses/submit"
            className="bg-gold text-white px-8 py-3.5 rounded-sm text-[15px] font-semibold no-underline font-sans transition-all inline-flex items-center justify-center gap-2 hover:bg-gold-light hover:-translate-y-0.5"
          >
            Submit Your Course
          </Link>
        </div>
      </section>

      {/* Mobile Filter Drawer */}
      {mobileFiltersOpen && (
        <div className="fixed inset-0 z-[100] md:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileFiltersOpen(false)} />
          <div className="absolute top-0 right-0 bottom-0 w-[85vw] max-w-[360px] bg-white overflow-y-auto animate-fade-in p-5">
            <FilterSidebar
              {...sidebarProps}
              onClose={() => setMobileFiltersOpen(false)}
            />
          </div>
        </div>
      )}
    </>
  );
}

// ═══ CARD WITH BANNER INSERTION ═══

function CourseCardWithBanner({
  course,
  index,
  showBanner,
  bannerInsertIndex,
}: {
  course: CourseRow;
  index: number;
  showBanner: boolean;
  bannerInsertIndex: number;
}) {
  return (
    <>
      {index === bannerInsertIndex && showBanner && <ProviderBanner />}
      <CourseCard course={course} />
    </>
  );
}

// ═══ COURSE CARD ═══

function CourseCard({ course }: { course: CourseRow }) {
  const router = useRouter();
  const gradient = getSeasonGradient(course.season_types);
  const initials = getProviderInitials(course.provider_name);
  const isFeatured = course.featured;

  const qualLabels = course.qualifications
    .map((q) => findLabelById(QUALIFICATIONS_TREE, q))
    .filter(Boolean)
    .slice(0, 3);
  const roleLabels = course.roles
    .map((r) => findLabelById(ROLES_TREE, r))
    .filter(Boolean)
    .slice(0, 3);
  const remainingRoles = course.roles.length - 3;

  const description = truncateDescription(course.description, 100);

  return (
    <div
      className={`group bg-white rounded-[18px] border overflow-hidden transition-all duration-200 flex flex-col cursor-pointer hover:-translate-y-1 hover:shadow-[0_12px_40px_rgba(0,0,0,0.08)] ${
        isFeatured
          ? "border-gold/50 shadow-[0_0_0_1px_rgba(28, 138, 153,0.15)] hover:border-gold/70"
          : "border-border-light shadow-sm hover:border-gold/40"
      }`}
      onClick={() => router.push(`/plan/courses/${course.id}`)}
    >
      <div className="px-4 py-4 sm:px-5 sm:py-4 flex-1 flex flex-col relative">
        {/* Featured badge — positioned top-right of card */}
        {isFeatured && (
          <div className="absolute top-3 right-3">
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-gold/12 text-gold border border-gold/25">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" stroke="none">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
              </svg>
              Featured
            </span>
          </div>
        )}

        {/* Top row: provider avatar + title */}
        <div className="flex items-center gap-3.5">
          <div
            className="w-12 h-12 rounded-[14px] flex items-center justify-center text-white text-[16px] font-bold shrink-0 shadow-[0_2px_8px_rgba(0,0,0,0.08)]"
            style={{
              background: `linear-gradient(135deg, ${gradient.from}, ${gradient.to})`,
            }}
          >
            {initials}
          </div>
          <div className="flex-1 min-w-0 pr-16">
            <h3 className="font-serif text-[16px] font-bold text-navy m-0 line-clamp-2 leading-snug">
              {course.title}
            </h3>
            <p className="text-[13px] text-text-muted mt-0.5 m-0 truncate">
              {course.provider_name}
            </p>
          </div>
        </div>

        {/* Description snippet */}
        {description && (
          <p className="text-[13px] text-text-muted leading-[1.5] mt-3 m-0 line-clamp-2">
            {description}
          </p>
        )}

        {/* Metadata lines with icons */}
        <div className="mt-3 space-y-1.5">
          {/* Format */}
          <div className="flex items-center gap-2 text-[12px] text-text-muted">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 text-text-muted/70">
              {course.format === "online" ? (
                <>
                  <rect x="2" y="3" width="20" height="14" rx="2" />
                  <line x1="8" y1="21" x2="16" y2="21" />
                  <line x1="12" y1="17" x2="12" y2="21" />
                </>
              ) : course.format === "in-person" ? (
                <>
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                  <circle cx="12" cy="10" r="3" />
                </>
              ) : (
                <>
                  <circle cx="12" cy="12" r="10" />
                  <path d="M2 12h20" />
                  <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                </>
              )}
            </svg>
            <span>{getFormatLabel(course.format)}</span>
          </div>

          {/* Location */}
          {course.location && (
            <div className="flex items-center gap-2 text-[12px] text-text-muted">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 text-text-muted/70">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
              <span className="truncate">{course.location}</span>
            </div>
          )}

          {/* Duration */}
          {course.duration && (
            <div className="flex items-center gap-2 text-[12px] text-text-muted">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 text-text-muted/70">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              <span>{course.duration}</span>
            </div>
          )}

          {/* Price */}
          {course.price_from != null && (
            <div className="flex items-center gap-2 text-[12px] text-charcoal font-medium">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 text-text-muted/70">
                <line x1="12" y1="1" x2="12" y2="23" />
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
              </svg>
              <span>From &pound;{course.price_from.toLocaleString()}</span>
            </div>
          )}
        </div>

        {/* Season + Qualification pills */}
        {(course.season_types.length > 0 || qualLabels.length > 0) && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {course.season_types.map((st) => {
              const style = getSeasonBadgeStyle(st);
              return (
                <span
                  key={st}
                  className="inline-block px-2.5 py-0.5 rounded-full text-[11px] font-semibold"
                  style={{ backgroundColor: style.bg, color: style.color }}
                >
                  {getSeasonLabel(st)}
                </span>
              );
            })}
            {qualLabels.map((q) => (
              <span
                key={q}
                className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-[#F3E8FF]/80 text-[#6B21A8] text-[11px] font-medium border border-[#E9D5FF]/50"
              >
                <GraduationCapIcon />
                {q}
              </span>
            ))}
          </div>
        )}

        {/* Role pills */}
        {roleLabels.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {roleLabels.map((r) => (
              <span
                key={r}
                className="px-2.5 py-1 rounded-full bg-navy/[0.05] text-navy/70 text-[11px] font-medium"
              >
                {r}
              </span>
            ))}
            {remainingRoles > 0 && (
              <span className="px-2.5 py-1 rounded-full bg-navy/[0.05] text-text-muted text-[11px] font-medium">
                +{remainingRoles}
              </span>
            )}
          </div>
        )}

        {/* View Details link */}
        <div className="pt-3 mt-auto">
          <span className="text-[13px] text-gold font-semibold">
            View Details &rarr;
          </span>
        </div>
      </div>
    </div>
  );
}

// ═══ FILTER CHIP ═══

function FilterChip({
  label,
  onRemove,
}: {
  label: string;
  onRemove: () => void;
}) {
  return (
    <button
      onClick={onRemove}
      className="inline-flex items-center gap-1.5 px-[11px] py-1 rounded-[20px] text-[12.5px] font-medium bg-cream text-navy border border-[rgba(0,0,0,0.10)] cursor-pointer transition-colors hover:bg-border font-sans"
    >
      {label}
      <span className="text-text-muted text-[14px] leading-none">
        &times;
      </span>
    </button>
  );
}
